import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './hooks/useAuth';
import { Login } from './pages/Login';
import { ForgotPassword } from './pages/ForgotPassword';
import { ResetPassword } from './pages/ResetPassword';
import { Onboarding } from './pages/Onboarding';
import { Dashboard } from './pages/Dashboard';
import { DayVideo } from './pages/DayVideo';
import { DayJournal } from './pages/DayJournal';
import { Admin } from './pages/Admin';
import { AdminDashboard } from './pages/admin/AdminDashboard';
import { MyJournal } from './pages/MyJournal';
import { CompletionFlow } from './pages/CompletionFlow';
import { CallDay } from './pages/CallDay';
import { ScrollToTop } from './components/ScrollToTop';
import './styles/global.css';

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <div style={{ display:'flex',alignItems:'center',justifyContent:'center',minHeight:'100vh',fontFamily:'Forum,serif',color:'#6B3E0A' }}>Loading...</div>;
  if (!user) return <Navigate to="/login" replace />;
  return children;
};

// ── NO COURSE BEFORE ONBOARDING (2026-09-28) ─────────────────────────────────
// Every course page sits behind this. A client who has not completed onboarding
// is sent to /onboarding, however they arrived: the portal home ("/" →
// /dashboard, which is where the re-engagement email links), a bookmark, a
// saved session, or a direct /day/N link. Until now only the Login page sent
// them to onboarding, so anyone still signed in could walk straight into Day 1.
//
// EXCEPTION — ALREADY IN THE COURSE: a client with ANY course progress
// (user.daysProgress, from GET /api/user/me) keeps the access they have, even
// if they were never marked onboarded. On 2026-09-28 that is exactly one
// person (katrinafitch55, who reached Day 1 by the old route). Nobody with
// progress is ever sent back to the start.
// Admins are not clients and pass straight through.
const CourseGate = ({ children }) => {
  const { user } = useAuth();
  const inCourse = Array.isArray(user?.daysProgress) && user.daysProgress.length > 0;
  if (user && !user.isAdmin && !user.onboardingComplete && !inCourse) {
    return <Navigate to="/onboarding" replace />;
  }
  return children;
};

const OnboardingGuard = ({ children }) => {
  const { user } = useAuth();
  if (user?.onboardingComplete) return <Navigate to="/dashboard" replace />;
  return children;
};

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <ScrollToTop />
        <Routes>
          <Route path="/login" element={<Login />} />
          {/* Public — used while logged out. Same pattern as /login (no guard). */}
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/onboarding" element={<ProtectedRoute><OnboardingGuard><Onboarding /></OnboardingGuard></ProtectedRoute>} />
          <Route path="/dashboard" element={<ProtectedRoute><CourseGate><Dashboard /></CourseGate></ProtectedRoute>} />
          <Route path="/day/:dayNum/video" element={<ProtectedRoute><CourseGate><DayVideo /></CourseGate></ProtectedRoute>} />
          <Route path="/day/:dayNum/journal" element={<ProtectedRoute><CourseGate><DayJournal /></CourseGate></ProtectedRoute>} />
          <Route path="/admin" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
          <Route path="/admin/clients" element={<ProtectedRoute><Admin /></ProtectedRoute>} />
          <Route path="/my-journal" element={<ProtectedRoute><CourseGate><MyJournal /></CourseGate></ProtectedRoute>} />
          <Route path="/completion" element={<ProtectedRoute><CourseGate><CompletionFlow /></CourseGate></ProtectedRoute>} />
          <Route path="/call-day" element={<ProtectedRoute><CourseGate><CallDay /></CourseGate></ProtectedRoute>} />
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}
